import { useId } from 'react';
import { t } from '../../lib/i18n';

/** True for a 24-hour "HH:MM" time. */
export function isTimeOfDay(v: string): boolean {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(v);
}

/** Minutes after midnight for "HH:MM", or null. */
export function timeToMinutes(v: string): number | null {
  return isTimeOfDay(v) ? Number(v.slice(0, 2)) * 60 + Number(v.slice(3)) : null;
}

export function minutesToTime(m: number): string {
  const c = ((Math.round(m) % 1440) + 1440) % 1440;
  return `${String(Math.floor(c / 60)).padStart(2, '0')}:${String(c % 60).padStart(2, '0')}`;
}

/**
 * Time-of-day field holding "HH:MM" (24-hour, no date or zone). Empty string means not set.
 * Uses the browser's own time control, which phones and screen readers handle well.
 */
export function TimeInput({
  label,
  value,
  onChange,
  hint,
  error,
  disabled,
  step = 60,
  id,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
  error?: string | undefined;
  disabled?: boolean;
  /** Seconds between steps. Default 60. */
  step?: number;
  id?: string;
}) {
  const auto = useId();
  const base = id ?? auto;
  const bad = error ?? (value !== '' && !isTimeOfDay(value) ? t('p2w.time.invalid') : undefined);
  const describedBy = [hint ? `${base}-hint` : '', bad ? `${base}-err` : ''].filter(Boolean).join(' ') || undefined;
  return (
    <div>
      <label htmlFor={base} className="mb-1 block text-sm font-medium text-slate-800">
        {label}
      </label>
      <input
        id={base}
        type="time"
        step={step}
        value={value}
        disabled={disabled}
        aria-invalid={bad ? true : undefined}
        aria-describedby={describedBy}
        onChange={(e) => onChange(e.target.value)}
        className="block min-h-10 w-40 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-900 hover:border-slate-400 disabled:bg-slate-100 aria-[invalid=true]:border-red-600 sm:min-h-9"
      />
      {hint ? (
        <p id={`${base}-hint`} className="mt-1 text-xs text-slate-600">
          {hint}
        </p>
      ) : null}
      {bad ? (
        <p id={`${base}-err`} className="mt-1 text-sm text-red-700">
          {bad}
        </p>
      ) : null}
    </div>
  );
}
